import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  DollarSign, 
  History, 
  Plus, 
  Minus,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  RefreshCw
} from 'lucide-react';

interface CreditTransaction {
  id: number;
  user: {
    id: number;
    username: string;
  };
  type: 'PURCHASE' | 'EARNED' | 'SPENT' | 'REFUND' | 'ADJUSTMENT';
  amount: number;
  description: string;
  createdAt: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
}

// Initialize Stripe
const stripePromise = loadStripe('pk_test_51OCHodIpizxN2fHkV9VrLOvlmXLIfPWFaYQSQvVAm0f3XglmOpzxLG1GoLSCRylLQjFELrKzAlp0yzE6pziUdQmG00PettzTh1');

interface PaymentResponse {
  chargeId: string;
  status: string;
  message: string;
}

// Stripe Payment Form Component
const StripePaymentForm: React.FC<{
  topUpAmount: number;
  onSuccess: (response: PaymentResponse) => void;
  onError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
}> = ({ topUpAmount, onSuccess, onError, isProcessing, setIsProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError('Stripe has not loaded yet');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onError('Card element not found');
      return;
    }

    try {
      setIsProcessing(true);
      
      // Create token from card element
      const { error, token } = await stripe.createToken(cardElement);
      
      if (error) {
        onError(error.message || 'Failed to create payment token');
        return;
      }

      if (!token) {
        onError('Failed to create payment token');
        return;
      }

      // Call backend to create payment intent
      const response = await apiService.createPaymentIntent({
        creditAmount: topUpAmount,
        token: token.id
      });

      const paymentData = response.data;
      
      if (paymentData.status === 'succeeded') {
        onSuccess(paymentData);
      } else {
        onError(paymentData.message || 'Payment failed');
      }
      
    } catch (error: any) {
      onError(error.response?.data?.message || 'Payment failed');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="border border-gray-300 rounded-md p-3 bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>
      
      <div className="text-sm text-gray-600">
        <p>Amount: <span className="font-semibold">${topUpAmount}</span> ({topUpAmount} credits)</p>
        <p className="text-xs mt-1">1 credit = $1 USD</p>
      </div>
      
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
            Processing Payment...
          </>
        ) : (
          `Pay $${topUpAmount}`
        )}
      </button>
    </form>
  );
};

const Payments: React.FC = () => {
  const { user, refreshAuth } = useAuth();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(10);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);


  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getCreditTransactions();
      console.log('Transactions response:', response.data);
      
      // Validate and clean transaction data
      const validTransactions = (response.data || []).filter(transaction => {
        if (!transaction || typeof transaction !== 'object') {
          console.warn('Invalid transaction object:', transaction);
          return false;
        }
        return true;
      });
      
      console.log('Valid transactions after filtering:', validTransactions);
      setTransactions(validTransactions);
    } catch (error: any) {
      toast.error('Failed to load transaction history');
      console.error('Error fetching transactions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshAuth = async () => {
    try {
      setIsRefreshing(true);
      const success = await refreshAuth();
      if (success) {
        toast.success('Authentication refreshed successfully!');
        // Refresh transactions after successful auth refresh
        await fetchTransactions();
      } else {
        toast.error('Failed to refresh authentication');
      }
    } catch (error) {
      toast.error('Error refreshing authentication');
      console.error('Error refreshing auth:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handlePaymentSuccess = (response: PaymentResponse) => {
    toast.success(response.message || 'Payment successful and credits added!');
    setShowTopUpModal(false);
    setTopUpAmount(10);
    fetchTransactions(); // Refresh transactions
    // Refresh user data to show updated credits
    window.location.reload();
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
  };

  const getTransactionIcon = (type: string) => {
    if (!type) return <DollarSign className="h-4 w-4 text-gray-600" />;
    
    switch (type) {
      case 'PURCHASE':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'EARNED':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'SPENT':
        return <Minus className="h-4 w-4 text-red-600" />;
      case 'REFUND':
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case 'ADJUSTMENT':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <DollarSign className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTransactionColor = (type: string) => {
    if (!type) return 'text-gray-600';
    
    switch (type) {
      case 'PURCHASE':
      case 'EARNED':
      case 'REFUND':
        return 'text-green-600';
      case 'SPENT':
        return 'text-red-600';
      case 'ADJUSTMENT':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Date unavailable';
    
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Convert technical transaction descriptions to user-friendly ones
  const getReadableTransactionDescription = (description: string, type: string) => {
    if (!description) return 'No description available';
    
    // Handle common patterns in transaction descriptions
    if (description.includes('Credits held for session request')) {
      return 'Credits reserved for session request';
    }
    if (description.includes('Credits released for session request')) {
      return 'Credits returned from session request';
    }
    if (description.includes('Credits received for accepting session request')) {
      return 'Credits earned from teaching session';
    }
    if (description.includes('Credits deducted for session')) {
      return 'Credits spent on learning session';
    }
    if (description.includes('Credits purchased')) {
      return 'Credits purchased';
    }
    if (description.includes('Credits refunded')) {
      return 'Credits refunded';
    }
    if (description.includes('Credits adjusted')) {
      return 'Credits adjusted by admin';
    }
    if (description.includes('Credits earned from feedback')) {
      return 'Credits earned from session feedback';
    }
    if (description.includes('Credits bonus')) {
      return 'Bonus credits received';
    }
    
    // For any other descriptions, try to make them more readable
    return description
      .replace(/Credits held for/, 'Credits reserved for')
      .replace(/Credits released for/, 'Credits returned from')
      .replace(/Credits received for accepting/, 'Credits earned from')
      .replace(/Credits deducted for/, 'Credits spent on')
      .replace(/Credits held for/, 'Credits reserved for')
      .replace(/session request #\d+/, 'session request')
      .replace(/\(\d+ credits?\)/, '')
      .trim();
  };

  // Convert transaction types to user-friendly names
  const getReadableTransactionType = (type: string) => {
    if (!type) return 'Unknown';
    
    switch (type) {
      case 'PURCHASE':
        return 'Purchase';
      case 'EARNED':
        return 'Earned';
      case 'SPENT':
        return 'Spent';
      case 'REFUND':
        return 'Refund';
      case 'ADJUSTMENT':
        return 'Adjustment';
      case 'HELD':
        return 'Reserved';
      case 'RELEASED':
        return 'Returned';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  };

  // Convert transaction amounts to user-friendly display
  const getReadableAmount = (type: string, amount: number) => {
    if (!type || amount === undefined || amount === null) {
      return (
        <span className="text-gray-600">
          {amount || 0} Credit{(amount || 0) !== 1 ? 's' : ''}
        </span>
      );
    }
    
    const prefix = type === 'SPENT' || type === 'HELD' ? '-' : '+';
    const color = type === 'SPENT' || type === 'HELD' ? 'text-red-600' : 'text-green-600';
    
    return (
      <span className={color}>
        {prefix}{amount} Credit{amount !== 1 ? 's' : ''}
      </span>
    );
  };

  // Convert transaction status to user-friendly names
  const getReadableStatus = (status: string) => {
    if (!status) return 'Unknown';
    
    switch (status) {
      case 'PENDING':
        return 'Processing';
      case 'COMPLETED':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };



  const getRecentTransactions = () => {
    // Filter out any transactions with missing required fields
    const validTransactions = transactions.filter(transaction => 
      transaction && 
      transaction.id && 
      (transaction.type || transaction.description || transaction.amount !== undefined)
    );
    return validTransactions.slice(0, 5);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Payments...</h2>
          <p className="text-gray-600">Preparing your financial data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold gradient-text">Credits & Payments</h1>
            <p className="text-gray-600 mt-2 text-lg">Manage your credits and payment history</p>
            <div className="mt-3 flex space-x-6">
              <a 
                href="/credit-history" 
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 transition-all duration-300 hover:scale-105"
              >
                <History className="w-4 h-4 mr-2" />
                View detailed credit history
              </a>
              <button
                onClick={handleRefreshAuth}
                className="inline-flex items-center text-sm text-green-600 hover:text-green-800 transition-all duration-300 hover:scale-105"
                disabled={isRefreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh Auth'}
              </button>
            </div>
          </div>
          <button
            onClick={() => setShowTopUpModal(true)}
            className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
          >
            <Plus className="h-5 w-5 mr-2" />
            Top Up Credits
          </button>
        </div>
      </div>

      {/* Credit Balance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Current Balance</p>
              <p className="text-2xl font-bold text-gray-900">{user?.credits || 0} Credits</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earned</p>
              <p className="text-2xl font-bold text-green-600">
                {transactions
                  .filter(t => t.type === 'EARNED')
                  .reduce((sum, t) => sum + t.amount, 0)} Credits
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Spent</p>
              <p className="text-2xl font-bold text-red-600">
                {transactions
                  .filter(t => t.type === 'SPENT')
                  .reduce((sum, t) => sum + t.amount, 0)} Credits
              </p>
            </div>
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
              <TrendingDown className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
          <a
            href="/credit-history"
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            View All
          </a>
        </div>

        <div className="space-y-4">
          {getRecentTransactions().length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No transactions yet</p>
            </div>
          ) : (
            getRecentTransactions().filter(transaction => transaction && transaction.id).map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                    {getTransactionIcon(transaction.type || 'UNKNOWN')}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{getReadableTransactionDescription(transaction.description || 'UNKNOWN', transaction.type || 'UNKNOWN')}</p>
                    <p className="text-sm text-gray-500">{transaction.createdAt ? formatDate(transaction.createdAt) : 'Date unavailable'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${getTransactionColor(transaction.type || 'UNKNOWN')}`}>
                    {getReadableAmount(transaction.type, transaction.amount)}
                  </p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status || 'UNKNOWN')}`}>
                    {getReadableStatus(transaction.status || 'UNKNOWN')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.filter(transaction => 
                transaction && 
                transaction.id && 
                (transaction.type || transaction.description || transaction.amount !== undefined)
              ).map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                        {getTransactionIcon(transaction.type || 'UNKNOWN')}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {getReadableTransactionType(transaction.type || 'UNKNOWN')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getReadableTransactionDescription(transaction.description || 'UNKNOWN', transaction.type || 'UNKNOWN')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-semibold ${getTransactionColor(transaction.type || 'UNKNOWN')}`}>
                      {getReadableAmount(transaction.type, transaction.amount)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status || 'UNKNOWN')}`}>
                      {getReadableStatus(transaction.status || 'UNKNOWN')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.createdAt ? formatDate(transaction.createdAt) : 'Date unavailable'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Up Credits</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (Credits)
                </label>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[5, 10, 20, 50, 100].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setTopUpAmount(amount)}
                      className={`p-3 border rounded-lg text-sm font-medium ${
                        topUpAmount === amount
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {amount} Credits
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="1"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-sm font-medium">{topUpAmount} Credits</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-gray-600">Cost:</span>
                  <span className="text-sm font-medium">${topUpAmount}.00 USD</span>
                </div>
                <div className="border-t border-gray-200 mt-2 pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-900">Total:</span>
                    <span className="text-sm font-bold text-gray-900">${topUpAmount}.00 USD</span>
                  </div>
                </div>
              </div>

              {/* Stripe Elements Form */}
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  topUpAmount={topUpAmount}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  isProcessing={isProcessingPayment}
                  setIsProcessing={setIsProcessingPayment}
                />
              </Elements>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTopUpModal(false);
                    setTopUpAmount(10);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments; 